import View       from '@widesk-ui/components/View';
import Alert      from '@widesk-ui/components/Alert';
import Button     from '@widesk-ui/components/Button';
import Select     from '@widesk-ui/components/form/Select';
import type { PlanCategory }      from '@/TokamapEditor/helpers/planLayerMapping';
import type { PlanMatchCandidate } from '@models/map/dxfTypes';

/**
 * **Sur quel champ reconnaître un POI déjà présent** (`docs/PLAN_PARCOURS_IMPORT.md` §5).
 *
 * ## Le défaut le plus coûteux du parcours
 *
 * `matchFieldMapping` partait VIDE au serveur, qui retombait alors sur `'label'`. Or sur un
 * plan de salon, `label` porte le nom de l'exposant et le numéro de stand vit dans `reference`
 * ou dans un champ custom. Conséquence, jamais signalée : chaque réimport d'un hall RECRÉAIT
 * les POI en doublon au lieu de les mettre à jour. On ne s'en apercevait qu'en comptant.
 *
 * ## Un choix sur un chiffre, pas sur une intuition
 *
 * Personne ne sait de tête où vivent les numéros. `POST /plan/match-preview` l'essaie : pour
 * chaque champ candidat, combien de POI existants les numéros lus sur le plan retrouveraient.
 * Le choix se fait donc sur une phrase de conséquence — « 141 mis à jour · 8 créés » — et un
 * candidat qui créerait des dizaines de POI se voit du premier coup d'œil.
 *
 * C'est le seul écran du parcours qui parle de la CARTE EXISTANTE et non du fichier.
 */
type Props = {
	categories: PlanCategory[];
	/** Candidats mesurés par le serveur, toutes catégories confondues. `null` = pas encore mesuré. */
	candidates: PlanMatchCandidate[] | null;
	/** Numéros lisibles trouvés sur le plan : le dénominateur de tout le tableau. */
	numbers: number;
	busy?: boolean;
	onMeasure: () => void;
	onChange: (categoryKey: string, field: string) => void;
};

/** Ce qu'un candidat produirait, dit en une phrase. */
function outcome(candidate: PlanMatchCandidate): string {
	return `${candidate.matched} mis à jour · ${candidate.created} créé(s)`;
}

const PlanMatchField = ({ categories, candidates, numbers, busy, onMeasure, onChange }: Props) => {
	const enabled = categories.filter(category => category.enabled && !!category.placeTypeUrn);

	if (!enabled.length) {
		return (
			<View size={11} color="#999">
				Cochez des lignes et donnez-leur un type de POI : le rapprochement se mesure sur le
				type choisi.
			</View>
		);
	}

	if (!candidates) {
		return (
			<View gap={6}>
				<View size={11} color="#999">
					Sans réglage, l’import reconnaît les POI existants sur leur <b>libellé</b>. Sur un
					plan de salon, le libellé porte le nom de l’exposant et non le numéro de stand :
					le réimport recrée alors les POI en doublon.
				</View>
				<Button size="small" loading={busy} onClick={onMeasure}>
					Mesurer les champs de rapprochement
				</Button>
			</View>
		);
	}

	return (
		<View gap={8}>
			<View size={11} color="#999">
				{numbers} numéro(s) lu(s) sur le plan. Un champ qui n’en retrouve aucun créera autant
				de doublons.
			</View>

			{enabled.map(category => {
				const ofCategory = candidates.filter(candidate => candidate.category === category.key);
				if (!ofCategory.length) {
					return (
						<View key={category.key} size={11} color="#999">
							{category.label} — aucun POI de ce type sur la carte : tout sera créé.
						</View>
					);
				}

				const recommended = ofCategory.find(candidate => candidate.recommended);
				// Le champ RETENU : celui de l'opérateur, à défaut le recommandé. Le second n'est
				// pas encore un choix — il n'est envoyé au serveur que si l'opérateur le confirme,
				// sinon on lui prêterait une décision qu'il n'a pas prise.
				const value = category.matchField || recommended?.field || '';
				const chosen = ofCategory.find(candidate => candidate.field === value);

				return (
					<View key={category.key} gap={4}>
						<View size={12} bold>{category.label}</View>
						<Select
							options={ofCategory.map(candidate => ({
								value: candidate.field,
								label: `${candidate.label} — ${outcome(candidate)}${candidate.recommended ? ' ✓' : ''}`,
							}))}
							placeholder="Champ de rapprochement"
							value={value || undefined}
							onChange={(next: unknown) => onChange(category.key, String(next ?? ''))}
						/>
						{chosen && chosen.created > 0 && chosen.matched < chosen.existing && (
							<Alert
								type="warning"
								showIcon
								message={`${chosen.created} POI seraient CRÉÉS`}
								description={
									`Sur ${chosen.existing} POI déjà sur la carte, ce champ n’en retrouve que `
									+ `${chosen.matched}. Les autres seront des doublons.`
								}
							/>
						)}
						{!category.matchField && (
							<View size={11} color="#999">
								Rien n’est envoyé tant que vous n’avez pas choisi : le serveur garderait
								son défaut (« libellé »).
							</View>
						)}
					</View>
				);
			})}
		</View>
	);
};

export default PlanMatchField;
